// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { InputProps } from 'react-autosuggest';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { alarmsAction, ActionDO } from '../../../requests';
import { command, CommandDO, variable, VariableDO } from '../../../../configuration/requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

interface Props {
  rowId?: number;
  notify: () => void;
}

interface State {
  loading: boolean;

  suggestions: CommandDO[];
  variableArray: VariableDO[];
  commandsArray: CommandDO[];

  command1?: string;
  command2?: string;

  data?: Partial<ActionDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private defaultValues: any;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,

      suggestions: [],
      variableArray: [],
      commandsArray: []
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      const [variableArray, commandsArray] = await Promise.all([
        variable<VariableDO[]>('list') || [],
        command<CommandDO[]>('list') || [],
      ]);
      let rowData: ActionDO | undefined;
      if (!isNew) {
        rowData = await alarmsAction<ActionDO>('get', String(rowId));
      }
      this.setState(
        {
          variableArray,
          commandsArray,
          data: rowData,
        },
        () => {
          this.defaultValues = _.cloneDeep(rowData);
        }
      );
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState({
      data: {
        ...this.defaultValues,
      }
    });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;
    if (!this.isValidForm(data)) return;

    this.loadingStart();
    alarmsAction<ActionDO>('set', undefined, data)
      ?.then((_resp: ActionDO) => {
        messageInfo('Success', `Alarm Action successfully ${data?.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('AAE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidForm = (data?: Partial<ActionDO>): boolean => {
    const { type } = data || {};
    if (!type) {
      messageError('Error', 'Please select an action type');
      return false;
    }
    return true;
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, tagName } = e.target;
    if (tagName === 'SELECT') {
      const [outerName, innerName] = name.split('.');
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
        },
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: value,
        },
      }));
    }
  };

  public getSuggestions = (value: string): CommandDO[] => {
    const { commandsArray } = this.state;

    const inputValue: string = value.trim().toLowerCase();
    const inputLength: number = inputValue.length;

    return inputLength === 0
      ? []
      : commandsArray.filter((comm: CommandDO) => comm.value.toLowerCase().slice(0, inputLength) === inputValue);
  };

  public getSuggestionValue = (suggestion: CommandDO): string => {
    return suggestion.value;
  }

  public renderSuggestion = (suggestion: CommandDO): JSX.Element => {
    return <div>{suggestion.value}</div>;
  }

  public handleChangeSuggestions1 = (_e: React.FormEvent<HTMLElement>, { newValue }: any): void => {
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        command1: newValue,
      },
    }));
  };

  public handleChangeSuggestions2 = (_e: React.FormEvent<HTMLElement>, { newValue }: any): void => {
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        command2: newValue,
      },
    }));
  };

  public handleSuggestionsFetchRequested = ({ value }: CommandDO): void => {
    this.setState({
      suggestions: this.getSuggestions(value),
    });
  };

  public handleSuggestionsClearRequested = (): void => {
    this.setState({
      suggestions: [],
    });
  };

  public render(): JSX.Element {
    const { loading, suggestions, data } = this.state;

    const inputProps1: InputProps<any> = {
      placeholder: 'Type a command',
      value: this.state.command1 || '',
      onChange: this.handleChangeSuggestions1,
    };

    const inputProps2: InputProps<any> = {
      placeholder: 'Type a command',
      value: this.state.command2 || '',
      onChange: this.handleChangeSuggestions2,
    };
    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget>
                <div className="widget-body">
                  <Loader show={this.state.loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Alarm Action {data?.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        onChange={this.handleChange}
                        formValues={this.state}
                        onChangeStandard={this.handleChange}
                        suggestions={suggestions}
                        onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
                        onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
                        getSuggestionValue={this.getSuggestionValue}
                        renderSuggestion={this.renderSuggestion}
                        inputProps1={inputProps1}
                        inputProps2={inputProps2}
                      />
                      <ButtonsForm loading={loading} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
